import type { ReactElement, SubmitEvent } from "react";
import { useParams } from "react-router";

import { useApiData } from "../../api/use-api-data.js";
import { usePageMeta } from "../../app/use-page-meta.js";
import { FormSummary } from "../../forms/fields.js";
import { planRules } from "../../forms/view-model-rules.js";

import { ConfirmationView } from "./confirmation-view.js";
import { CheckField } from "./contract-fields.js";
import type { Confirmation, ContractKind, PlanForm } from "./contract-types.js";
import { LoadingPage } from "./loading-page.js";
import { invoiceSelectable, isOn, needsPayment } from "./plan-form.js";
import { PlanOptions } from "./plan-options.js";
import { PlanSelection, PlanTotal } from "./plan-selection.js";
import { tenantApi, tenantUrls } from "./urls.js";
import { useContractFlow, type ContractFlow } from "./use-contract-flow.js";

const inputTitle = "プラン変更";

/** 最後の確認はブラウザの確認ダイアログで行う（重要な操作は最後にダイアログで確かめる。2026-10-10 に利用者が決めた）。 */
export const planChangeDialog = (value: Confirmation): string =>
    `「${value.tenantTitle ?? ""}」のプランを${value.planDisplayName}に変更します。`
    + `変更後の月額（税込）は ${value.priceLabel} で、次回のお支払い日から適用されます。よろしいですか？`;

/** プラン変更（<c>/Tenant/ChangePlan/{契約}</c> → 確認 → 確定）。 */
export const planChangeKind = (tenantId: string): ContractKind => ({
    inputTitle,
    confirmTitle: "変更内容の確認",
    confirmLead: "",
    trialNote: "変更しても期間は延びません。",
    confirmNote: "新しい月額料金は、次回のお支払い日から適用されます。お支払い済みの料金の差額は返金いたしかねます。",
    cardButton: "カード情報を入力して変更する",
    submitButton: "変更を確定する",
    confirmUrl: tenantApi.planConfirm(tenantId),
    commitUrl: tenantApi.plan(tenantId),
    rules: planRules,
    dialog: planChangeDialog,
});

/** 支払方法。<b>無料のプランでは支払いが発生しないので、カードごと出さない。</b> */
function PaymentSection({ flow }: { readonly flow: ContractFlow }): ReactElement | null {
    if (!needsPayment(flow.plan)) {
        return null;
    }

    return (
        <section className="card mb-7 p-6" data-plan-payment="">
            <h2 className="section-title">お支払方法</h2>
            {invoiceSelectable(flow.plan)
                ? (
                    <CheckField
                        label="請求書払いを希望する" name="PaymentOnInvoice"
                        checked={isOn(flow.values, "PaymentOnInvoice")} onChange={flow.change}
                    />
                )
                : null}
        </section>
    );
}

function PlanChangeInput({ form, flow }: {
    readonly form: PlanForm;
    readonly flow: ContractFlow;
}): ReactElement {
    usePageMeta(inputTitle);
    const section = {
        plans: form.plans,
        plan: flow.plan,
        values: flow.values,
        showInitialStorage: form.showInitialStorage,
        fieldError: flow.fieldError,
        onChange: flow.change,
        onSelectPlan: flow.selectPlan,
    };

    const onSubmit = (event: SubmitEvent<HTMLFormElement>): void => {
        event.preventDefault();
        flow.confirm(event.currentTarget);
    };

    return (
        <div className="page max-w-3xl">
            <h1 className="page-title">{inputTitle}</h1>
            <p className="page-lead">{form.tenantTitle}</p>

            <form
                method="post" noValidate onSubmit={onSubmit}
                onBlur={(event) => { flow.leave(event.target.getAttribute("name") ?? ""); }}
            >
                <FormSummary messages={flow.summary} />

                <div className="alert-info mb-5">
                    下位のプランへの変更、利用人数・ストレージ容量の減少はできません。
                </div>

                <PlanTotal plan={flow.plan} values={flow.values} />
                <PlanSelection {...section} />
                <PlanOptions {...section} />
                <PaymentSection flow={flow} />

                <div className="flex gap-3">
                    <a href={tenantUrls.list} className="btn-secondary">キャンセル</a>
                    <button type="submit" className="btn-primary grow" disabled={flow.busy}>確認画面へ</button>
                </div>
            </form>
        </div>
    );
}

function PlanChangeFlow({ form, kind }: {
    readonly form: PlanForm;
    readonly kind: ContractKind;
}): ReactElement {
    const flow = useContractFlow(form, kind, []);

    return flow.step === "confirm"
        ? <ConfirmationView flow={flow} kind={kind} />
        : <PlanChangeInput form={form} flow={flow} />;
}

/**
 * プラン変更。<b>契約者だけ</b>——それ以外は API が 404 を返す。
 * <b>変更する契約は URL で決まる</b>（フォームの TenantId は API が使わない）。
 * ご契約者の連絡先（有償プランで必須）は入力しない。未登録なら API が断り、先頭の断りの文に出る。
 */
export function ChangePlanPage(): ReactElement {
    const tenantId = useParams()["tenantId"] ?? "";
    const data = useApiData<PlanForm>(tenantApi.plan(tenantId));

    return data.state === "ready"
        ? <PlanChangeFlow form={data.value} kind={planChangeKind(tenantId)} />
        : <LoadingPage title={inputTitle} data={data} />;
}
